<template>
  <div
    class="container v-content-container tooltip-wrapper"
    :key="componentKey"
  >
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-list-alt"></i>
          External venues
          <router-link
            class="btn btn-v-lightgreen-white pull-right"
            :to="{ name: 'NewEventVenue' }"
            v-tooltip="{
              content: $label('event.create_new_venue', 'Create new venue'),
              classes: 'custom-tooltip'
            }"
          >
            <i class="fa fa-fw fa-lg fa-plus"></i>
          </router-link>
        </h1>
      </div>
    </div>

    <navigation-tabs></navigation-tabs>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <alert-box
      v-show="failedRequest && !loading"
      type="warning"
      :closeable="false"
      :show-icon="false"
    >
      {{
        $label(
          "event.event_failed_request",
          "Request failed while loading external venues."
        )
      }}
    </alert-box>

    <Table
      v-show="!failedRequest"
      id="external-venues-table"
      :loading="loading"
      :columns="[
        {
          title: 'Name',
          field: 'name',
          id: row => `venue-name-${row.id}`
        },
        {
          title: 'Place',
          id: row => `venue-place-${row.id}`,
          value: row =>
            row.address ? `${row.address.post_code} ${row.address.city}` : ''
        },
        {
          title: 'Street',
          id: row => `venue-street-${row.id}`,
          value: row => (row.address ? row.address.street_name : '')
        }
      ]"
      :actions="[
        {
          type: 'detail',
          onClick: row => {
            $router.push({
              name: 'EventVenueDetails',
              params: {
                venue_id: row.id
              }
            });
          }
        },
        {
          type: 'delete',
          id: row => `delete-venue-button-${row.id}`,
          onClick: row => deleteVenue(row.id)
        }
      ]"
      :data="venues"
      :meta="metaData"
      @fetchQuery="
        params => {
          fetchParams = params;
          getVenues({
            //
          });
        }
      "
    >
      <div
        id="action-dropdown"
        slot="toolbar"
        class="btn-group"
        style="margin-left: 5px;"
      >
        <button
          type="button"
          class="btn btn-primary dropdown-toggle"
          data-toggle="dropdown"
        >
          <i class="fa fa-refresh"></i> &nbsp;
          {{ $label("common.actions", "Actions") }} &nbsp;<span
            class="caret"
          ></span>
        </button>
        <ul class="dropdown-menu" role="menu">
          <li>
            <router-link :to="{ name: 'NewEventVenue' }"
              ><i class="text-primary fa fa-fw fa-lg fa-plus"></i>
              {{
                $label("event.create_new_venue", "Create new venue")
              }}</router-link
            >
          </li>
        </ul>
      </div>
    </Table>

    <div class="row mb-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>
  </div>
</template>

<script>
import Api from "@/api.js";
import swal from "sweetalert";
import FormErrorsMixin from "@/mixins/FormErrors";

import NavigationTabs from "@/components/Settings/Events/NavigationTabs.vue";
import Table from "@/components/Common/Table";
import AlertBox from "@/components/Common/Alerts/AlertBox";

export default {
  name: "EventVenues.vue",

  mixins: [FormErrorsMixin],

  components: {
    Table,
    NavigationTabs,
    AlertBox
  },

  data() {
    return {
      venues: [],
      loading: false,
      updating: false,
      failedRequest: false,
      metaData: null,
      fetchParams: {
        page: 1,
        per_page: null,
        search: ""
      },
      componentKey: 0
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (
        vm.$route.query.venue_deleted === true ||
        vm.$route.query.venue_deleted === "true"
      ) {
        swal("Success!", "The venue was deleted.", "success").then(() => {
          window.history.pushState(
            {},
            document.title,
            window.location.pathname
          );
        });
      }
      vm.getVenues(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (to.query.venue_deleted === true) {
      swal("Success!", "The venue was deleted.", "success").then(() => {
        window.history.pushState({}, document.title, window.location.pathname);
      });
    }
    this.forceRerender();
    if (
      this.fetchParams.to % this.fetchParams.per_page === 0 &&
      this.fetchParams.page !== 1
    ) {
      this.fetchParams.page -= 1;
    }
    this.getVenues(() => {
      //
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    getVenues(successCallback) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.venues = [];
      const endpoint = this.$partnerEndpoint("venues");

      Api()
        .get(endpoint, {
          params: {
            page: this.fetchParams.page,
            perPage: this.fetchParams.per_page,
            search: this.fetchParams.search,
            type: "external"
          }
        })
        .then(response => {
          this.venues = response.data.data;
          this.metaData = response.data.meta;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(() => {
          this.failedRequest = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    deleteVenue(venueId) {
      if (this.loading || this.updating || venueId == null) {
        return;
      }

      swal({
        title: "Are you sure?",
        text:
          "The external venue will be deleted and will not be able to use it",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint("venues/" + venueId);

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router
                .push({
                  name: "EventVenues",
                  query: { venue_deleted: true }
                })
                .catch(error => {
                  console.log(error);
                  if (
                    error.name === "NavigationDuplicated" ||
                    error.message.includes(
                      "Avoided redundant navigation to current location"
                    )
                  ) {
                    swal("Success!", "The venue was deleted.", "success");
                    this.getVenues(() => {
                      //
                    });
                  }
                });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete external venues.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    forceRerender() {
      this.componentKey += 1;
    }
  }
};
</script>

<style scoped>
.mt-0 {
  margin-top: 0;
}

.mt-8 {
  margin-top: 1.3rem;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-1 {
  margin-bottom: 0.3rem;
}

.mb-2 {
  margin-bottom: 0.6rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}
</style>
